import { useEffect, useMemo, useState } from "react";
import { AppButton, FilterBar, FilterDrawer } from "../../../../components/ui";
import {
  Autocomplete,
  Box,
  CircularProgress,
  createFilterOptions,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Dayjs } from "dayjs";
import {
  buildActiveChips,
  clearFilterField,
  countActiveFilters,
  hasActiveFilters,
  resetFilters,
} from "./filtering";
import {
  DEFAULT_SORT_RULES,
  FILTER_FIELD_LABELS,
  SORT_DIRECTION_OPTIONS,
  SORT_FIELD_OPTIONS,
  type FilterFieldKey,
  type LloCasesToFormFiltersState,
  type SortDirection,
  type SortRule,
  type SortableColumn,
} from "./types";

type FilterOptions = {
  caseName: string[];
  stage: string[];
  raa: string[];
  ap: string[];
};

type LloCasesToFormFiltersProps = {
  filters: LloCasesToFormFiltersState;
  filterOptions: FilterOptions;
  sortRules: SortRule[];
  loading: boolean;
  downloading: boolean;
  canDownload: boolean;
  onLoad: () => void;
  onApply: (next: LloCasesToFormFiltersState, sortRules: SortRule[]) => void;
  onDownload: () => void;
  onClear: () => void;
};

const ALL_OPTION = "All";

const stringFilterOptions = createFilterOptions<string>({
  matchFrom: "any",
  stringify: (option) => option,
  trim: true,
  ignoreCase: true,
});

function FilterAutocomplete({
  field,
  value,
  options,
  onChange,
}: {
  field: FilterFieldKey;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  const optionsWithAll = useMemo(
    () => [ALL_OPTION, ...options.filter((o) => o !== ALL_OPTION)],
    [options]
  );

  return (
    <Autocomplete
      size="small"
      options={optionsWithAll}
      value={value || ALL_OPTION}
      onChange={(_, next) => onChange(!next || next === ALL_OPTION ? "" : next)}
      filterOptions={stringFilterOptions}
      autoHighlight
      clearOnBlur={false}
      handleHomeEndKeys
      ListboxProps={{ style: { maxHeight: 280 } }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={FILTER_FIELD_LABELS[field]}
          placeholder="Type to filter…"
        />
      )}
      fullWidth
    />
  );
}

function DateRangeFields({
  label,
  from,
  to,
  onFromChange,
  onToChange,
}: {
  label: string;
  from: Dayjs | null;
  to: Dayjs | null;
  onFromChange: (value: Dayjs | null) => void;
  onToChange: (value: Dayjs | null) => void;
}) {
  return (
    <Box>
      <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.75 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1}>
        <DatePicker
          label="From"
          value={from}
          onChange={onFromChange}
          slotProps={{ textField: { size: "small", fullWidth: true } }}
        />
        <DatePicker
          label="To"
          value={to}
          onChange={onToChange}
          slotProps={{ textField: { size: "small", fullWidth: true } }}
        />
      </Stack>
    </Box>
  );
}

function SortRulesEditor({
  rules,
  onChange,
}: {
  rules: SortRule[];
  onChange: (rules: SortRule[]) => void;
}) {
  const usedColumns = useMemo(() => new Set(rules.map((rule) => rule.column)), [rules]);

  const updateRule = (index: number, patch: Partial<SortRule>) => {
    onChange(rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
  };

  const removeRule = (index: number) => {
    if (rules.length <= 1) return;
    onChange(rules.filter((_, i) => i !== index));
  };

  const addRule = () => {
    const next = SORT_FIELD_OPTIONS.find((option) => !usedColumns.has(option.value));
    if (!next) return;
    onChange([...rules, { column: next.value, direction: "asc" }]);
  };

  return (
    <Box>
      <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.5 }}>
        Sort by
      </Typography>
      <Stack spacing={1}>
        {rules.map((rule, index) => (
          <Stack key={`${rule.column}-${index}`} direction="row" spacing={1} alignItems="center">
            <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
              <InputLabel id={`llo-form-sort-col-${index}`}>Column</InputLabel>
              <Select
                labelId={`llo-form-sort-col-${index}`}
                label="Column"
                value={rule.column}
                onChange={(e) => updateRule(index, { column: e.target.value as SortableColumn })}
              >
                {SORT_FIELD_OPTIONS.filter(
                  (option) => option.value === rule.column || !usedColumns.has(option.value)
                ).map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ width: 140 }}>
              <InputLabel id={`llo-form-sort-dir-${index}`}>Direction</InputLabel>
              <Select
                labelId={`llo-form-sort-dir-${index}`}
                label="Direction"
                value={rule.direction}
                onChange={(e) =>
                  updateRule(index, { direction: e.target.value as SortDirection })
                }
              >
                {SORT_DIRECTION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton
              size="small"
              aria-label="Remove sort rule"
              onClick={() => removeRule(index)}
              disabled={rules.length <= 1}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <AppButton
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={addRule}
          disabled={usedColumns.size >= SORT_FIELD_OPTIONS.length}
          sx={{ alignSelf: "flex-start" }}
        >
          Add sort
        </AppButton>
      </Stack>
    </Box>
  );
}

export function LloCasesToFormFilters({
  filters,
  filterOptions,
  sortRules,
  loading,
  downloading,
  canDownload,
  onLoad,
  onApply,
  onDownload,
  onClear,
}: LloCasesToFormFiltersProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draft, setDraft] = useState<LloCasesToFormFiltersState>(filters);
  const [draftSortRules, setDraftSortRules] = useState<SortRule[]>(sortRules);
  const filtersActive = hasActiveFilters(filters);
  const draftActive = hasActiveFilters(draft);
  const activeCount = countActiveFilters(filters);
  const activeChips = buildActiveChips(filters);

  useEffect(() => {
    if (drawerOpen) {
      setDraft(filters);
      setDraftSortRules(sortRules.length > 0 ? sortRules : [...DEFAULT_SORT_RULES]);
    }
  }, [drawerOpen, filters, sortRules]);

  const patchDraft = (patch: Partial<LloCasesToFormFiltersState>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  };

  const clearField = (key: keyof LloCasesToFormFiltersState) => {
    onApply(clearFilterField(filters, key), sortRules);
  };

  const handleApply = () => {
    onApply(draft, draftSortRules.length > 0 ? draftSortRules : [...DEFAULT_SORT_RULES]);
    setDrawerOpen(false);
  };

  const clearDraft = () => setDraft(resetFilters());

  return (
    <>
      <FilterBar
        leading={
          <>
            <AppButton variant="contained" size="small" onClick={onLoad} disabled={loading}>
              {loading ? <CircularProgress size={18} color="inherit" /> : "Load cases"}
            </AppButton>
            <AppButton
              variant="outlined"
              size="small"
              startIcon={downloading ? <CircularProgress size={16} /> : <FileDownloadIcon />}
              onClick={onDownload}
              disabled={downloading || loading || !canDownload}
              sx={{ bgcolor: "background.paper" }}
            >
              Download
            </AppButton>
          </>
        }
        activeCount={activeCount}
        filtersActive={filtersActive}
        onOpenFilters={() => setDrawerOpen(true)}
        onClear={onClear}
        clearDisabled={loading || !filtersActive}
        chips={activeChips.map((chip) => ({
          key: String(chip.key),
          label: chip.label,
          onDelete: () => clearField(chip.key),
        }))}
      />

      <FilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Filters & sort"
        subtitle="Apply to update the table"
        activeCount={countActiveFilters(draft)}
        actions={
          <>
            <AppButton
              variant="outlined"
              color="warning"
              fullWidth
              startIcon={<FilterAltOffIcon />}
              onClick={clearDraft}
              disabled={!draftActive}
            >
              Clear filters
            </AppButton>
            <AppButton variant="contained" fullWidth onClick={handleApply} disabled={loading}>
              Apply
            </AppButton>
          </>
        }
      >
        <Stack spacing={1.75}>
          <TextField
            size="small"
            label={FILTER_FIELD_LABELS.searchTerm}
            placeholder="Search…"
            value={draft.searchTerm}
            onChange={(e) => patchDraft({ searchTerm: e.target.value })}
            fullWidth
          />
          <FilterAutocomplete
            field="caseName"
            value={draft.caseName}
            options={filterOptions.caseName}
            onChange={(value) => patchDraft({ caseName: value })}
          />
          <FilterAutocomplete
            field="stage"
            value={draft.stage}
            options={filterOptions.stage}
            onChange={(value) => patchDraft({ stage: value })}
          />
          <FilterAutocomplete
            field="raa"
            value={draft.raa}
            options={filterOptions.raa}
            onChange={(value) => patchDraft({ raa: value })}
          />
          <FilterAutocomplete
            field="ap"
            value={draft.ap}
            options={filterOptions.ap}
            onChange={(value) => patchDraft({ ap: value })}
          />
          <DateRangeFields
            label="Complaint filed"
            from={draft.complaintFiledFrom}
            to={draft.complaintFiledTo}
            onFromChange={(value) => patchDraft({ complaintFiledFrom: value })}
            onToChange={(value) => patchDraft({ complaintFiledTo: value })}
          />
          <DateRangeFields
            label="Form by"
            from={draft.formByFrom}
            to={draft.formByTo}
            onFromChange={(value) => patchDraft({ formByFrom: value })}
            onToChange={(value) => patchDraft({ formByTo: value })}
          />
          <SortRulesEditor rules={draftSortRules} onChange={setDraftSortRules} />
        </Stack>
      </FilterDrawer>
    </>
  );
}
